import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { ComponentProps, useEffect, useLayoutEffect, useRef } from 'react';

import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import ChevronLeftIcon from '@flipster/react/icon/generated/ChevronLeftIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import PlusIcon from '@flipster/react/icon/generated/PlusIcon';
import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { useIsElementInViewport } from '@flipster/react/misc/browser/intersection';
import { useCheckScrollable } from '@flipster/react/misc/browser/scrollable';
import { Styles, css } from '@flipster/styled-system/css';
import { divider, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

import { feedCardBackgroundColorVarKey } from '../constant';
import { FeedPositionCard } from '../feed-card/position-card';
import { feedPostAndEditBunja, maxContentLength } from './bunja';

const PostContentPresets = [
  'Nice move 🔥',
  'To the moon 🌕 ',
  'Pump it up! 🚀',
  'Quick flip ⚡',
  'Stop hit 🛑',
  'Cut losses ✂️',
  'Oh no, not again. 😭 ',
  'Tough day😞',
  'Volatility is wild! 🌪️',
  'Watch and wait 👀',
];

interface FeedPostFormProps {
  submitLabel: string;
  onComplete: () => void;
  onCancel?: () => void;
  css?: Styles;
}
export const FeedPostEditForm = ({ submitLabel, onComplete, onCancel, css: cssProps }: FeedPostFormProps) => {
  const t = useTranslations();
  const {
    isEditing,
    isLoggedInAtom,
    ineligibilityAtom,
    textContentAtom,
    textContentLengthAtom,
    dirtyAtom,
    postErrorAtom,
    lastTradeInfoAtom,
    addLastTrade,
    clearLastTrade,
    submit,
    subscribePostError,
  } = useBunja(feedPostAndEditBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const ineligibility = useAtomValue(ineligibilityAtom);
  const [textContent, setTextContent] = useAtom(textContentAtom);
  const textContentLength = useAtomValue(textContentLengthAtom);
  const lastTradeInfo = useAtomValue(lastTradeInfoAtom);
  const dirty = useAtomValue(dirtyAtom);
  const postError = useAtomValue(postErrorAtom);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const presetContainerRef = useRef<HTMLDivElement>(null);
  const errorMessageRef = useRef<HTMLParagraphElement>(null);

  const isScrollable = useCheckScrollable('horizontal', presetContainerRef);
  const [leftRef, isVisibleLeft] = useIsElementInViewport<HTMLDivElement>({ threshold: 0.5 });
  const [rightRef, isVisibleRight] = useIsElementInViewport<HTMLDivElement>({ threshold: 0.5 });
  const handleChevronButtonClick = (direction: 'left' | 'right') => {
    if (!presetContainerRef.current) return;
    const current = presetContainerRef.current.scrollLeft;
    const next = direction === 'left' ? current - 150 : current + 150;
    presetContainerRef.current.scrollTo({ left: next, behavior: 'smooth' });
  };

  useEffect(() => {
    return subscribePostError((next, prev) => {
      if (next == null || next != prev) return;
      errorMessageRef.current?.animate({ opacity: [1, 0, 1], offset: [0, 0.5], easing: 'ease-in-out' }, 300);
    });
  }, [subscribePostError]);

  useLayoutEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'inherit'; // for the shrink case
    textareaRef.current.style.height = `${Math.min(textareaRef.current?.scrollHeight, lastTradeInfo ? 110 : 307)}px`;
  }, [lastTradeInfo, textContent]);

  const placeholder = (() => {
    if (!isLoggedIn) return 'Sign in to join the feed';
    return ineligibility?.message ?? 'Post a comment';
  })();

  async function submitForm() {
    await submit(onComplete);
  }

  return (
    <div className={css(stack.raw({ gap: 12 }), cssProps)}>
      <div
        className={stack({
          gap: 16,
          backgroundColor: 'background.default.level-1',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-3',
          rounded: 4,
          p: '16px 12px',
          minH: 106,
        })}
      >
        <textarea
          ref={textareaRef}
          value={textContent}
          rows={1}
          onChange={(e) => setTextContent(e.currentTarget.value)}
          onClick={() => segment.track(CommunityEvent.ShareYourThoughtsClick)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.metaKey) submitForm();
          }}
          disabled={Boolean(ineligibility)}
          placeholder={placeholder}
          className={css({
            resize: 'none',
            width: '100%',
            backgroundColor: 'transparent',
            border: 'none',
            caretColor: 'blue.500',
            textStyle: 'Text/L/Regular',
            color: 'content.default.level-5',
            scrollbarWidth: 'thin',
          })}
        />
        {lastTradeInfo && (
          <div className={css({ pos: 'relative', maxW: 343 })}>
            <FeedPositionCard
              type='EDIT'
              item={lastTradeInfo}
              css={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'border.default.level-2' }}
            />
            <button className={css({ pos: 'absolute', top: -12, right: -12, p: 4 })} onClick={clearLastTrade}>
              <XCircleIcon size={24} color={token('colors.content.default.level-1')} />
            </button>
          </div>
        )}
        {!isLoggedIn && (
          <div className={hstack({ textStyle: 'Text/M/Regular', justifyContent: 'flex-end' })}>
            <Button
              as='a'
              href='/login'
              label={t('Autogen_common.r251126.s_Sign_up_Log_in_to_post')}
              onClick={() => segment.track(CommunityEvent.FeedSignupToPostClick)}
            />
          </div>
        )}
        {!ineligibility && (
          <div className={hstack({ textStyle: 'Text/M/Regular', justifyContent: 'space-between', gap: 16 })}>
            <p className={css({ color: 'content.state.negative' })} ref={errorMessageRef}>
              {textContentLength > maxContentLength
                ? t('Autogen_common.r251126.s_Post_too_long_Limit_is_maxContentLength_character', {
                    maxContentLength: maxContentLength,
                  })
                : postError}
            </p>
            <div className={hstack({ gap: 16 })}>
              <p className={css({ color: 'content.default.level-2' })}>
                {textContentLength >= 10000 ? '9,999+' : textContentLength.toLocaleString('en-US')} / {maxContentLength}
              </p>
              <div className={hstack({ gap: 8 })}>
                {onCancel && (
                  <Button hierarchy='secondary' color='neutral' label={t('Common.r250401.Cancel')} onClick={onCancel} />
                )}
                <Button
                  label={submitLabel}
                  onClick={submitForm}
                  withSpinner
                  disabled={textContentLength > maxContentLength || (isEditing && !dirty)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {isLoggedIn && (
        <div className={hstack({ gap: 12 })}>
          <TabButton
            css={{ textStyle: 'Text/M/Strong' }}
            onClick={addLastTrade}
            disabled={isEditing || Boolean(lastTradeInfo)}
          >
            <PlusIcon size={16} />
            Latest trade
          </TabButton>
          <hr className={divider({ orientation: 'vertical', color: 'border.default.level-2', h: 24 })} />
          <div className={css({ pos: 'relative', minW: 0 })}>
            <div className={hstack({ overflowX: 'scroll' })} ref={presetContainerRef}>
              <div ref={leftRef} />
              <div className={hstack({ gap: 8, flexShrink: 0 })}>
                {PostContentPresets.map((preset) => (
                  <TabButton
                    key={preset}
                    onClick={() => {
                      if (!isLoggedIn) return snackbar.error({ message: 'Sign in to join the feed' });
                      setTextContent((textContent.length ? `${textContent}\n` : '') + preset);
                      textareaRef.current?.focus();
                    }}
                  >
                    {preset}
                  </TabButton>
                ))}
              </div>
              <div ref={rightRef} className={css({ pl: 1 })} />
            </div>
            {isScrollable && !isVisibleLeft && <ChevronLeftButton onClick={() => handleChevronButtonClick('left')} />}
            {isScrollable && !isVisibleRight && (
              <ChevronRightButton onClick={() => handleChevronButtonClick('right')} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface TabButtonProps extends ComponentProps<'button'> {
  css?: Styles;
}
const TabButton = ({ css: cssProps, children, disabled, ...props }: TabButtonProps) => {
  return (
    <button
      {...props}
      disabled={disabled}
      className={css(
        hstack.raw(),
        {
          gap: 2,
          flexShrink: 0,
          rounded: '9999px',
          p: '8px 16px',
          backgroundColor: 'background.accent.secondary.tint',
          color: 'content.default.level-5',
          textStyle: 'Text/M/Regular',
          cursor: 'pointer',
        },
        disabled && {
          backgroundColor: 'background.interaction.disabled',
          color: 'content.interaction.disabled',
        },
        cssProps,
      )}
    >
      {children}
    </button>
  );
};

interface ChevronButtonProps {
  onClick: () => void;
}
const ChevronLeftButton = ({ onClick }: ChevronButtonProps) => {
  return (
    <div
      className={hstack({
        pos: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
      })}
    >
      <button
        className={css({ h: '100%', cursor: 'pointer' })}
        style={{
          backgroundColor: `var(${feedCardBackgroundColorVarKey}, ${token('colors.background.default.level-0')})`,
        }}
        onClick={onClick}
      >
        <ChevronLeftIcon size={20} color={token('colors.content.default.level-2')} />
      </button>
      <div
        className={css({
          w: 16,
          h: '100%',
        })}
        style={{
          background: `linear-gradient(to left, transparent, var(${feedCardBackgroundColorVarKey}, ${token('colors.background.default.level-0')}))`,
        }}
      />
    </div>
  );
};

const ChevronRightButton = ({ onClick }: ChevronButtonProps) => {
  return (
    <div
      className={hstack({
        pos: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
      })}
    >
      <div
        className={css({
          w: 16,
          h: '100%',
        })}
        style={{
          background: `linear-gradient(to right, transparent, var(${feedCardBackgroundColorVarKey}, ${token('colors.background.default.level-0')}))`,
        }}
      />
      <button
        className={css({ h: '100%', cursor: 'pointer' })}
        style={{
          backgroundColor: `var(${feedCardBackgroundColorVarKey}, ${token('colors.background.default.level-0')})`,
        }}
        onClick={onClick}
      >
        <ChevronRightIcon size={20} color={token('colors.content.default.level-2')} />
      </button>
    </div>
  );
};
